import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CoreDataService } from '@app/core/async-services/http/core.data';
import { ApiVersionedRoute } from '@app/core/async-services/http/versioned/api-versions';
import { API } from '@app/core/models';
import {
  CreateOrEditRoomCleanTaskNoteBody,
  CreateOrEditTaskModel,
  CreateTaskBody,
  DeleteTaskBody,
  EditTaskBody,
  GetTaskInboxChannelSuccess,
  NewTaskDataModel,
  Note,
  RoomCleanTaskListPayload,
  Task,
  TaskDetailsParams,
  TaskDetailsPayload,
  TaskList,
  TaskListPayload,
  TaskStatusUpdatedData,
  UpdateRoomCleanTaskAssigneeBody,
  UpdateRoomCleanTaskAssigneePayload,
  UpdateTaskStatusBody,
  UpdateTaskStatusPayload,
} from './task-management.interface';

@Injectable({
  providedIn: 'root',
})
export class TaskManagementHttpService {
  constructor(private _coreDataService: CoreDataService) {}

  getNewTaskData(entId: string): Observable<NewTaskDataModel> {
    const options = this._coreDataService.getHttpOptions({ entId });
    return this._coreDataService
      .get<API.Response<NewTaskDataModel>>(
        `${ApiVersionedRoute.task_management}/new-task-data`,
        options,
      )
      .pipe(map((response) => response.payload as NewTaskDataModel));
  }

  createTask(body: CreateTaskBody): Observable<Task> {
    const options = this._coreDataService.getHttpOptions();
    return this._coreDataService
      .post<API.Response<CreateOrEditTaskModel>>(
        `${ApiVersionedRoute.task_management}/create-task`,
        body,
        options,
      )
      .pipe(map((response) => response.payload?.task as Task));
  }

  updateTaskStatus(
    body: UpdateTaskStatusBody,
  ): Observable<TaskStatusUpdatedData> {
    const options = this._coreDataService.getHttpOptions();
    return this._coreDataService
      .post<API.Response<UpdateTaskStatusPayload>>(
        `${ApiVersionedRoute.task_management}/update-task-status`,
        body,
        options,
      )
      .pipe(
        map(
          (response) =>
            response.payload?.taskStatusUpdatedData as TaskStatusUpdatedData,
        ),
      );
  }

  getTaskDetails(params: TaskDetailsParams): Observable<TaskDetailsPayload> {
    const options = this._coreDataService.getHttpOptions(params);
    return this._coreDataService
      .get<API.Response<TaskDetailsPayload>>(
        `${ApiVersionedRoute.task_management}/task-details`,
        options,
      )
      .pipe(map((response) => response.payload as TaskDetailsPayload));
  }

  getTaskInboxChannel(): Observable<string> {
    const options = this._coreDataService.getHttpOptions({});
    return this._coreDataService
      .get<GetTaskInboxChannelSuccess>(
        `${ApiVersionedRoute.task_management}/task-inbox-channel`,
        options,
      )
      .pipe(map((response) => response.payload.taskInboxChannelName));
  }

  getRoomCleanTaskInboxChannel(): Observable<string> {
    const options = this._coreDataService.getHttpOptions({
      type: 'roomCleans',
    });
    return this._coreDataService
      .get<GetTaskInboxChannelSuccess>(
        `${ApiVersionedRoute.task_management}/task-inbox-channel`,
        options,
      )
      .pipe(map((response) => response.payload.taskInboxChannelName));
  }

  deleteTask(
    body: DeleteTaskBody,
  ): Observable<API.Response<Record<string, never>>> {
    const options = this._coreDataService.getHttpOptions();
    return this._coreDataService.post<API.Response<Record<string, never>>>(
      `${ApiVersionedRoute.task_management}/delete-task`,
      body,
      options,
    );
  }

  getTaskList(entId: string): Observable<TaskList> {
    const options = this._coreDataService.getHttpOptions({ entId });
    return this._coreDataService
      .get<API.Response<TaskListPayload>>(
        `${ApiVersionedRoute.task_management}/task-list`,
        options,
      )
      .pipe(map((response) => response.payload?.taskList as TaskList));
  }

  getRoomCleanTaskList(entId: string): Observable<RoomCleanTaskListPayload> {
    const options = this._coreDataService.getHttpOptions({ entId });
    return this._coreDataService
      .get<API.Response<RoomCleanTaskListPayload>>(
        `${ApiVersionedRoute.task_management}/room-clean-task-list`,
        options,
      )
      .pipe(map((response) => response.payload as RoomCleanTaskListPayload));
  }

  editTask(body: Partial<EditTaskBody>): Observable<Task> {
    const options = this._coreDataService.getHttpOptions();
    return this._coreDataService
      .post<API.Response<CreateOrEditTaskModel>>(
        `${ApiVersionedRoute.task_management}/edit-task`,
        body,
        options,
      )
      .pipe(map((response) => response.payload?.task as Task));
  }

  updateRoomCleanTaskAssignee(
    body: UpdateRoomCleanTaskAssigneeBody,
  ): Observable<UpdateRoomCleanTaskAssigneePayload> {
    const options = this._coreDataService.getHttpOptions();
    return this._coreDataService
      .post<API.Response<UpdateRoomCleanTaskAssigneePayload>>(
        `${ApiVersionedRoute.task_management}/update-room-clean-task-assignee`,
        body,
        options,
      )
      .pipe(
        map(
          (response) => response.payload as UpdateRoomCleanTaskAssigneePayload,
        ),
      );
  }

  createOrEditTaskNote(
    body: CreateOrEditRoomCleanTaskNoteBody,
  ): Observable<Note> {
    const options = this._coreDataService.getHttpOptions();
    return this._coreDataService
      .post<API.Response<Note>>(
        `${ApiVersionedRoute.task_management}/create-or-edit-task-note`,
        body,
        options,
      )
      .pipe(map((response) => response.payload as Note));
  }

  markAllNotesAsRead(taskId: string): Observable<null> {
    const options = this._coreDataService.getHttpOptions();
    return this._coreDataService.post<null>(
      `${ApiVersionedRoute.task_management}/mark-all-notes-as-read`,
      { taskId },
      options,
    );
  }
}
